import React, { useContext, useEffect, useState } from "react";
import { useAppStore } from "../../../../../context/store";
import { Dialog, Portal, Field, CloseButton, Button, For, Input, VStack, HStack, SimpleGrid, Spacer, Editable, Spinner, Text, Box, Table } from "@chakra-ui/react";
import { serviceBackup } from "../../../../../api/services/backup";
import { formatISODateSimple, showToast } from "../../../../../utilies/Utility";
import Type from "../../../../../utilies/Types";
import { LuFolderDown } from "react-icons/lu";
import { load3DTiles } from "../../../../../utilies/loader/Load3DTiles";
import { loadGeojsonImageType } from "../../../../../utilies/loader/LoadCamera";
import { loadGeojsonTypeAsync } from "../../../../../utilies/loader/LoadGeojson";
import { loadCSVTypeAsync } from "../../../../../utilies/loader/LoadCSV";
import { loadOsmTypeAsync } from "../../../../../utilies/loader/LoadOSM";
import { loadGlbTypeAsync } from "../../../../../utilies/loader/LoadGLB";
import JSZip from "jszip";
import { useMapObjects } from "../../../../../context/MapObjectsContext";

const ProjectBackupList = () => {
  // state
  const originalCameraControls = useAppStore((s) => s.originalCameraControls);
  const isShowBackupListDialog = useAppStore((s) => s.isShowBackupListDialog);
  const userData = useAppStore((s) => s.userData);
  const viewerRef = useAppStore((s) => s.viewerRef);
  const defaultGeojsonStyle = useAppStore((s) => s.defaultGeojsonStyle);
  const basicGeojsonStyle = useAppStore((s) => s.basicGeojsonStyle);
  const defaultOsmStyle = useAppStore((s) => s.defaultOsmStyle);

  // setters
  const setIsShowBackupListDialog = useAppStore.getState().setIsShowBackupListDialog;
  const setImageGeojson = useAppStore.getState().setImageGeojson;
  const setImageGeojsonSelected = useAppStore.getState().setImageGeojsonSelected;
  const setImageGeojsonMode = useAppStore.getState().setImageGeojsonMode;
  const { mapObjects, setMapObjects, mapObjectsRef } = useMapObjects();
  const setImageGeojsonCounter = useAppStore.getState().setImageGeojsonCounter;
  const setImageDataSource = useAppStore.getState().setImageDataSource;
  const setActiveLayer = useAppStore.getState().setActiveLayer;

  const [list, setList] = useState([]);
  const [isProgress, setIsProgress] = useState(true);

  const importFromUrl = async (inputUrl = null, inputType = null) => {
    let localUrl = inputUrl;
    let localType = inputType;

    try {
      switch (localType) {
        case Type.ASSET.THREEDTILES:
          await load3DTiles({ url: localUrl, viewerRef, setMapObjects });
          break;
        case Type.ASSET.GEOJSON_IMAGE:
          fetch(localUrl)
            .then((response) => {
              if (!response.ok) {
              }
              return response.json();
            })
            .then((data) => {
              let geoJsonData = data;
              loadGeojsonImageType({
                viewerRef,
                setImageGeojson,
                setImageGeojsonSelected,
                setImageGeojsonMode,
                setImageDataSource,
                setMapObjects,
                setImageGeojsonCounter,
                geojsonData: geoJsonData,
                fileName: localUrl,
              });
            })
            .catch((error) => {});
          break;
        case Type.ASSET.GEOJSON:
          fetch(localUrl)
            .then((response) => {
              if (!response.ok) {
              }
              return response.json();
            })
            .then((data) => {
              let geoJsonData = data;
              loadGeojsonTypeAsync({
                originalCameraControls,
                geojsonData: geoJsonData,
                fileName: localUrl,
                viewerRef,
                setMapObjects,
                defaultGeojsonStyle,
                basicGeojsonStyle,
                isUrl: true,
                defaultOsmStyle,
              });
            })
            .catch((error) => {});
          break;
        case Type.ASSET.CSV:
          fetch(localUrl)
            .then((response) => {
              if (!response.ok) {
                throw new Error("Network response was not ok");
              }
              return response.text();
            })
            .then(async (csvText) => {
              await loadCSVTypeAsync({
                text: csvText,
                fileName: localUrl,
                viewerRef,
                setMapObjects,
                defaultGeojsonStyle,
              });
            })
            .catch((error) => {
              console.error("Error loading CSV:", error);
            });
          break;
        case Type.ASSET.OSM:
          fetch(localUrl)
            .then((response) => {
              if (!response.ok) {
                throw new Error("Network response was not ok");
              }
              return response.text();
            })
            .then((text) => {
              const encoder = new TextEncoder();
              const arrayBuffer = encoder.encode(text).buffer;
              loadOsmTypeAsync({
                arrayBuffer: arrayBuffer,
                fileName: localUrl,
                viewerRef,
                setMapObjects,
                setActiveLayer,
                defaultOsmStyle,
                fileFormat: "osm",
                defaultGeojsonStyle,
                basicGeojsonStyle,
              });
            })
            .catch((error) => {
              console.error("Fetch error:", error);
            });
          break;
        case Type.ASSET.ZIP:
          fetch(localUrl)
            .then((response) => {
              if (!response.ok) {
                throw new Error("Failed to fetch zip");
              }
              return response.arrayBuffer();
            })
            .then((zipData) => JSZip.loadAsync(zipData))
            .then(async (zip) => {
              for (const relativePath in zip.files) {
                const file = zip.files[relativePath];
                if (!file.dir) {
                  try {
                    const fileData = await file.async("string");

                    if (file.name.endsWith(".geojson")) {
                      loadGeojsonTypeAsync({
                        originalCameraControls,
                        geojsonData: JSON.parse(fileData),
                        fileName: file.name,
                        viewerRef,
                        setMapObjects,
                        defaultGeojsonStyle,
                        basicGeojsonStyle,
                        defaultOsmStyle,
                      });
                    } else if (file.name.endsWith(".glb")) {
                      const glbBuffer = await file.async("arraybuffer");
                      loadGlbTypeAsync({
                        arrayBuffer: glbBuffer,
                        fileName: file.name,
                        viewerRef,
                        setMapObjects,
                        positionDegrees: { lon: -112.068096, lat: 33.450842, height: 0 },
                        orientationDegrees: { heading: 0, pitch: 0, roll: 0 },
                        scale: 1.0,
                      });
                    }
                  } catch (err) {
                    console.error("Error reading file:", file.name, err);
                  }
                }
              }
            })
            .catch((error) => {
              console.error("Error loading zip file:", error);
            });
          break;
        case Type.ASSET.GLB:
          fetch(localUrl)
            .then((response) => {
              if (!response.ok) {
                throw new Error("Failed to fetch GLB file");
              }
              return response.arrayBuffer();
            })
            .then((glbData) => {
              loadGlbTypeAsync({
                arrayBuffer: glbData,
                fileName: localUrl,
                viewerRef,
                setMapObjects,
                positionDegrees: { lon: -112.068096, lat: 33.450842, height: 0 },
                orientationDegrees: { heading: 0, pitch: 0, roll: 0 },
                scale: 1.0,
              });
            })
            .catch((error) => {
              console.error("Error loading GLB file:", error);
            });

          break;
      }
    } catch (error) {
      console.error(`Error creating tileset: ${error}`);
    }
  };

  const getBackups = (projectId) => {
    setIsProgress(true);
    const formData = new FormData();
    formData.append("username", userData.user.username);
    formData.append("project_id", projectId);
    formData.append("action", "list");
    serviceBackup(formData)
      .then((res) => {
        setIsProgress(false);
        if (res.data.status == "success") {
          setList(res.data.backups);
        } else {
          showToast(res.data.message, Type.NOTIFICATION.WARNING);
        }
      })
      .catch((err) => {
        setIsProgress(false);
        console.error("Error", err);
        showToast(err.message, Type.NOTIFICATION.ERROR);
      });
  };

  useEffect(() => {
    if (isShowBackupListDialog && isShowBackupListDialog.open) {
      getBackups(isShowBackupListDialog.project_id);
    } else {
      setList([]);
    }
  }, [isShowBackupListDialog]);

  return (
    <Dialog.Root
      lazyMount
      closeOnInteractOutside={false}
      open={!!(isShowBackupListDialog && isShowBackupListDialog.open)}
      onOpenChange={(e) => {
        setIsShowBackupListDialog({
          ...isShowBackupListDialog,
          open: e.open,
        });
      }}
    >
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content>
            <Dialog.Header>
              <VStack gap={0}>
                <Dialog.Title textAlign={"left"} width={"full"}>
                  Backup List
                </Dialog.Title>
              </VStack>
            </Dialog.Header>

            <Dialog.Body>
              {isProgress ? (
                <Spinner size={"sm"} />
              ) : (
                <Table.Root size="sm" striped>
                  <Table.Header>
                    <Table.Row>
                      <Table.ColumnHeader>User</Table.ColumnHeader>
                      <Table.ColumnHeader>Date Time</Table.ColumnHeader>
                      <Table.ColumnHeader>Project Name</Table.ColumnHeader>
                      <Table.ColumnHeader textAlign="end">Actions</Table.ColumnHeader>
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    {list.map((item, index) => (
                      <Table.Row key={index}>
                        <Table.Cell>{item.user}</Table.Cell>
                        <Table.Cell>{formatISODateSimple(item.created_at)}</Table.Cell>
                        <Table.Cell>{item.project_name}</Table.Cell>
                        <Table.Cell textAlign="end">
                          <Button
                            bg={"transparent"}
                            onClick={() => {
                              importFromUrl(item.backup_file_url, Type.ASSET.ZIP);
                            }}
                          >
                            <LuFolderDown color="MenuText" />
                          </Button>
                        </Table.Cell>
                      </Table.Row>
                    ))}
                  </Table.Body>
                </Table.Root>
              )}
            </Dialog.Body>

            <Dialog.CloseTrigger asChild>
              <CloseButton size="sm" />
            </Dialog.CloseTrigger>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
};

export default ProjectBackupList;
